import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { LogOut, Menu, X, UserCircle2, UserCog } from "lucide-react";
import SponsorInquiryModal, { openSponsorForm } from "../SponsorInquiryModal";
import ideathonMark from "../../assets/ideathon-mark.png";

export default function Navbar() {
  const { user, logout, config, impersonating, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const onLogout = () => { logout(); setOpen(false); navigate("/"); };
  const onReturnAdmin = async () => {
    try { await stopImpersonating(); setOpen(false); navigate("/admin"); }
    catch { navigate("/admin"); }
  };
  const isPreLaunch = config?.status === "pre_launch";
  const accountEmail = user ? (user.college_email || user.personal_email || null) : null;
  const dashboardHref = user
    ? user.role === "judge"
      ? "/judge/dashboard"
      : user.role === "admin"
        ? "/admin"
        : user.team_id ? "/team/dashboard" : "/team"
    : "/";
  const hideStudentNav = isPreLaunch || (user && user.role !== "student" && user.role != null);

  return (
    <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="navbar">
      {impersonating && (
        <div className="bg-[#0A66C2] text-white text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap" data-testid="impersonation-banner">
          <span className="flex items-center gap-2"><UserCog className="w-4 h-4" /> Admin preview — you are viewing as <b>Test Student</b>.</span>
          <button onClick={onReturnAdmin} className="underline font-semibold hover:opacity-80" data-testid="return-to-admin-btn">Return to admin</button>
        </div>
      )}
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="group flex items-center gap-2.5" data-testid="logo-link">
          <img src={ideathonMark} alt="NoobSync Innovate logo" className="h-9 w-auto shrink-0" width="34" height="36" />
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-xl tracking-tighter text-white">NoobSync</span>
              <span className="font-display text-xl tracking-tighter text-brand">Innovate</span>
            </div>
            <div className="label-eyebrow mt-0.5">IdeaThon Edition</div>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-8 text-sm">
          {!hideStudentNav && (
            <>
              <a href="/#about" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-about">About</a>
              <a href="/#how" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-how">How It Works</a>
              <a href="/#timeline" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-timeline">Timeline</a>
              <a href="/#certificates" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-certificates">Certificates</a>
              <a href="/#faq" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
            </>
          )}
          <Link to="/editions" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-editions">Editions</Link>
          {!hideStudentNav && (
            <button onClick={openSponsorForm} className="text-brand hover:text-white transition-colors" data-testid="nav-sponsor">
              Sponsor Us
            </button>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/profile" className="flex items-center gap-2 pl-1 pr-3 py-1 border border-white/10 rounded-full max-w-[240px] hover:border-brand/50 transition-colors" data-testid="nav-account">
                <UserCircle2 className="w-5 h-5 text-brand flex-shrink-0" strokeWidth={1.5} />
                <div className="min-w-0 leading-tight">
                  <div className="text-xs text-white truncate" data-testid="nav-account-name">{user.name}{user.role && user.role !== "student" ? <span className="ml-1 text-[10px] uppercase tracking-wider text-brand">{user.role}</span> : null}</div>
                  {accountEmail && <div className="text-[10px] text-gray-500 font-mono truncate" data-testid="nav-account-email" title={accountEmail}>{accountEmail}</div>}
                </div>
              </Link>
              <Link to={dashboardHref} className="text-gray-400 hover:text-white transition-colors" data-testid="nav-dashboard">Dashboard</Link>
              <button onClick={onLogout} className="btn-secondary flex items-center gap-2 !py-2 !px-3" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Log out
              </button>
            </div>
          ) : (
            !isPreLaunch && (
              <>
                <Link to="/login" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-login">Log in</Link>
                <Link to="/register" className="btn-primary" data-testid="nav-register">Register Now</Link>
              </>
            )
          )}
        </nav>
        <button onClick={() => setOpen(!open)} className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0A0F1E]" data-testid="mobile-menu">
          <div className="flex flex-col p-5 gap-4 text-sm">
            {!hideStudentNav && (
              <>
                <a href="/#about"   onClick={() => setOpen(false)} className="text-gray-400">About</a>
                <a href="/#how"     onClick={() => setOpen(false)} className="text-gray-400">How It Works</a>
                <a href="/#timeline" onClick={() => setOpen(false)} className="text-gray-400">Timeline</a>
                <a href="/#certificates" onClick={() => setOpen(false)} className="text-gray-400">Certificates</a>
                <a href="/#faq"     onClick={() => setOpen(false)} className="text-gray-400">FAQ</a>
              </>
            )}
            <Link to="/editions" onClick={() => setOpen(false)} className="text-gray-400">Editions</Link>
            {!hideStudentNav && (
              <button onClick={() => { setOpen(false); openSponsorForm(); }} className="text-brand text-left" data-testid="mobile-nav-sponsor">
                Sponsor Us
              </button>
            )}
            {user ? (
              <>
                <div className="flex items-center gap-2 pb-3 mb-1 border-b border-white/10" data-testid="mobile-nav-account">
                  <UserCircle2 className="w-7 h-7 text-brand flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0 leading-tight">
                    <div className="text-sm text-white truncate">{user.name}{user.role && user.role !== "student" ? <span className="ml-1 text-[10px] uppercase tracking-wider text-brand">{user.role}</span> : null}</div>
                    {accountEmail && <div className="text-[11px] text-gray-500 font-mono truncate" title={accountEmail}>{accountEmail}</div>}
                  </div>
                </div>
                <Link to={dashboardHref} onClick={() => setOpen(false)} className="text-gray-400">Dashboard</Link>
                <button onClick={onLogout} className="btn-secondary">Log out</button>
              </>
            ) : (
              !isPreLaunch && (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-gray-400">Log in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-center">Register Now</Link>
                </>
              )
            )}
          </div>
        </div>
      )}
      <SponsorInquiryModal />
    </header>
  );
}
